import type {
  LayoutLetterMetadata,
  LayoutWordMetadata,
} from '../layout/LayoutProtocol.js'
import type { Offset2D } from '../project/ProjectDocument.js'
import type { EditorViewport } from '../store/commands.js'

export const MIN_CANVAS_ZOOM = 0.2
export const MAX_CANVAS_ZOOM = 5
export const CANVAS_ZOOM_SENSITIVITY = 0.001
export const DOT_HIT_PADDING = 5
export const MAX_LAYOUT_OVERLAY_ITEMS = 2_000
export const KEYBOARD_PAN_PIXELS = 32
export const KEYBOARD_ZOOM_FACTOR = 1.1

export interface ClientRectView {
  readonly left: number
  readonly top: number
  readonly width: number
  readonly height: number
}

export interface CanvasPresentation {
  readonly left: number
  readonly top: number
  readonly width: number
  readonly height: number
  readonly scale: number
}

export interface LayoutPoint {
  readonly x: number
  readonly y: number
}

export interface LayoutInteractionMetadata {
  readonly words: readonly LayoutWordMetadata[]
  readonly letters: readonly LayoutLetterMetadata[]
  readonly dots: {
    readonly count: number
    readonly x: NumericView
    readonly y: NumericView
    readonly width: NumericView
    readonly height: NumericView
    readonly relativeX: NumericView
    readonly relativeY: NumericView
    readonly wordIndex: NumericView
    readonly letterIndex: NumericView
  }
}

type NumericView = ArrayLike<number> | Readonly<{ length: number; at(index: number): number | undefined }>

function numericAt(view: NumericView, index: number): number | undefined {
  return 'at' in view ? view.at(index) : view[index]
}

export interface LayoutHitTestOptions {
  readonly letterMode: boolean
  readonly dotMode: boolean
  /** Live (unaccepted) offsets, matching what the overlay renders with. */
  readonly wordOffsets: readonly Readonly<Offset2D>[]
  readonly letterOffsets: readonly Readonly<Offset2D>[]
  readonly dotOffsets: readonly Readonly<Offset2D>[]
  /**
   * Offsets of the accepted layout the dot metadata was baked with. Dot hit
   * boxes must add the same (live − accepted) deltas the overlay renderer
   * applies, or they ghost at the pre-drag position after an unaccepted move.
   */
  readonly acceptedWordOffsets: readonly Readonly<Offset2D>[]
  readonly acceptedLetterOffsets: readonly Readonly<Offset2D>[]
  readonly acceptedDotOffsets: readonly Readonly<Offset2D>[]
  readonly dotHitPadding?: number
}

export type LayoutHit = Readonly<{
  kind: 'word' | 'letter' | 'dot'
  index: number
}>

export interface DragUpdate {
  readonly index: number
  readonly offset: Readonly<Offset2D>
}

export function dragUpdatesChanged(
  initialOffsets: readonly Readonly<Offset2D>[],
  updates: readonly DragUpdate[],
): boolean {
  return updates.some(({ index, offset }) => {
    const initial = offsetAt(initialOffsets, index)
    return !Object.is(initial.x, offset.x) || !Object.is(initial.y, offset.y)
  })
}

export function canvasPresentation(
  host: ClientRectView,
  canvasWidth: number,
  canvasHeight: number,
): CanvasPresentation {
  if (
    !Number.isFinite(host.left)
    || !Number.isFinite(host.top)
    || !Number.isFinite(host.width)
    || !Number.isFinite(host.height)
    || host.width <= 0
    || host.height <= 0
    || !Number.isFinite(canvasWidth)
    || !Number.isFinite(canvasHeight)
    || canvasWidth <= 0
    || canvasHeight <= 0
  ) throw new RangeError('Canvas presentation dimensions must be finite and positive')
  const scale = Math.min(host.width / canvasWidth, host.height / canvasHeight)
  const width = canvasWidth * scale
  const height = canvasHeight * scale
  return {
    left: host.left + (host.width - width) * 0.5,
    top: host.top + (host.height - height) * 0.5,
    width,
    height,
    scale,
  }
}

/** Inverts `center + (layout - center + pan) * zoom`, then the CSS object-fit scale. */
export function screenToLayoutPoint(
  screenX: number,
  screenY: number,
  presentation: CanvasPresentation,
  viewport: Readonly<EditorViewport>,
): LayoutPoint {
  const zoom = Number.isFinite(viewport.zoom) && viewport.zoom > 0 ? viewport.zoom : 1
  const centerX = presentation.width / presentation.scale * 0.5
  const centerY = presentation.height / presentation.scale * 0.5
  const surfaceX = (screenX - presentation.left) / presentation.scale
  const surfaceY = (screenY - presentation.top) / presentation.scale
  return {
    x: centerX + (surfaceX - centerX) / zoom - viewport.x,
    y: centerY + (surfaceY - centerY) / zoom - viewport.y,
  }
}

export function zoomFromWheel(currentZoom: number, deltaY: number): number {
  if (!Number.isFinite(currentZoom) || currentZoom <= 0) return 1
  const current = currentZoom
  if (!Number.isFinite(deltaY)) return Math.max(MIN_CANVAS_ZOOM, Math.min(MAX_CANVAS_ZOOM, current))
  const next = current + (-deltaY * CANVAS_ZOOM_SENSITIVITY) * current
  return Math.max(MIN_CANVAS_ZOOM, Math.min(MAX_CANVAS_ZOOM, next))
}

export function zoomFromKeyboard(currentZoom: number, direction: -1 | 1): number {
  const current = Number.isFinite(currentZoom) && currentZoom > 0 ? currentZoom : 1
  const next = direction > 0 ? current * KEYBOARD_ZOOM_FACTOR : current / KEYBOARD_ZOOM_FACTOR
  return Math.max(MIN_CANVAS_ZOOM, Math.min(MAX_CANVAS_ZOOM, next))
}

export function panViewport(
  viewport: Readonly<EditorViewport>,
  screenDeltaX: number,
  screenDeltaY: number,
  presentationScale: number,
): EditorViewport {
  const zoom = Number.isFinite(viewport.zoom) && viewport.zoom > 0 ? viewport.zoom : 1
  const scale = Number.isFinite(presentationScale) && presentationScale > 0 ? presentationScale : 1
  return {
    x: viewport.x + screenDeltaX / scale / zoom,
    y: viewport.y + screenDeltaY / scale / zoom,
    zoom,
  }
}

export function keyboardPanViewport(
  viewport: Readonly<EditorViewport>,
  key: string,
  presentationScale: number,
  accelerated: boolean,
): EditorViewport | null {
  const distance = KEYBOARD_PAN_PIXELS * (accelerated ? 4 : 1)
  if (key === 'ArrowLeft') return panViewport(viewport, -distance, 0, presentationScale)
  if (key === 'ArrowRight') return panViewport(viewport, distance, 0, presentationScale)
  if (key === 'ArrowUp') return panViewport(viewport, 0, -distance, presentationScale)
  if (key === 'ArrowDown') return panViewport(viewport, 0, distance, presentationScale)
  return null
}

function offsetAt(offsets: readonly Readonly<Offset2D>[], index: number): Readonly<Offset2D> {
  return offsets[index] ?? ZERO_OFFSET
}

/** Live − accepted offset delta for one owner; shared with the overlay renderer. */
export function offsetDelta(
  current: readonly Readonly<Offset2D>[],
  accepted: readonly Readonly<Offset2D>[],
  index: number | undefined,
): Readonly<Offset2D> {
  if (index === undefined) return ZERO_OFFSET
  const currentOffset = current[index] ?? ZERO_OFFSET
  const acceptedOffset = accepted[index] ?? ZERO_OFFSET
  return { x: currentOffset.x - acceptedOffset.x, y: currentOffset.y - acceptedOffset.y }
}

function contains(
  x: number,
  y: number,
  left: number,
  top: number,
  width: number,
  height: number,
  padding = 0,
): boolean {
  return x >= left - padding
    && x <= left + width + padding
    && y >= top - padding
    && y <= top + height + padding
}

const ZERO_OFFSET = Object.freeze({ x: 0, y: 0 })

export function isLayoutOwner(
  metadata: LayoutInteractionMetadata,
  target: 'word' | 'letter' | 'dot',
  index: number,
): boolean {
  if (!Number.isSafeInteger(index) || index < 0) return false
  if (target === 'word') return metadata.words[index]?.index === index
  if (target === 'letter') return metadata.letters[index]?.index === index
  return index < metadata.dots.count
    && numericAt(metadata.dots.x, index) !== undefined
    && numericAt(metadata.dots.y, index) !== undefined
    && numericAt(metadata.dots.width, index) !== undefined
    && numericAt(metadata.dots.height, index) !== undefined
    && numericAt(metadata.dots.wordIndex, index) !== undefined
    && numericAt(metadata.dots.letterIndex, index) !== undefined
}

export function hitTestLayout(
  metadata: LayoutInteractionMetadata,
  x: number,
  y: number,
  options: LayoutHitTestOptions,
): LayoutHit | null {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null
  // The overlay paints in metadata order, so the LAST matching entry is the
  // visually topmost one. Nastaliq boxes deliberately overlap; iterate in
  // reverse paint order so the ink the user actually sees gets selected.
  if (options.dotMode) {
    const padding = options.dotHitPadding ?? DOT_HIT_PADDING
    for (let index = metadata.dots.count - 1; index >= 0; index -= 1) {
      const centerX = numericAt(metadata.dots.x, index)
      const centerY = numericAt(metadata.dots.y, index)
      const width = numericAt(metadata.dots.width, index)
      const height = numericAt(metadata.dots.height, index)
      if (
        centerX === undefined
        || centerY === undefined
        || width === undefined
        || height === undefined
      ) continue
      // Dot metadata is baked with the ACCEPTED layout's offsets; the overlay
      // renders at baked + (live − accepted), so the hit box must follow.
      const wordDelta = offsetDelta(
        options.wordOffsets,
        options.acceptedWordOffsets,
        numericAt(metadata.dots.wordIndex, index),
      )
      const letterDelta = offsetDelta(
        options.letterOffsets,
        options.acceptedLetterOffsets,
        numericAt(metadata.dots.letterIndex, index),
      )
      const dotDelta = offsetDelta(options.dotOffsets, options.acceptedDotOffsets, index)
      const renderedX = centerX + wordDelta.x + letterDelta.x + dotDelta.x
      const renderedY = centerY + wordDelta.y + letterDelta.y + dotDelta.y
      if (contains(x, y, renderedX - width * 0.5, renderedY - height * 0.5, width, height, padding)) {
        return { kind: 'dot', index }
      }
    }
  }
  if (options.letterMode) {
    for (let position = metadata.letters.length - 1; position >= 0; position -= 1) {
      const letter = metadata.letters[position]
      if (letter === undefined) continue
      const wordOffset = offsetAt(options.wordOffsets, letter.wordIndex)
      const letterOffset = offsetAt(options.letterOffsets, letter.index)
      if (contains(
        x,
        y,
        letter.x + wordOffset.x + letterOffset.x,
        letter.y + wordOffset.y + letterOffset.y,
        letter.width,
        letter.height,
      )) return { kind: 'letter', index: letter.index }
    }
    return null
  }
  for (let position = metadata.words.length - 1; position >= 0; position -= 1) {
    const word = metadata.words[position]
    if (word === undefined) continue
    const offset = offsetAt(options.wordOffsets, word.index)
    if (contains(x, y, word.x + offset.x, word.y + offset.y, word.width, word.height)) {
      return { kind: 'word', index: word.index }
    }
  }
  return null
}

export function resolveDragUpdates(
  target: 'word' | 'letter' | 'dot',
  selectedIndex: number,
  deltaX: number,
  deltaY: number,
  initialOffsets: readonly Readonly<Offset2D>[],
  metadata: LayoutInteractionMetadata,
  pushAdjacent: boolean,
): readonly DragUpdate[] {
  if (!isLayoutOwner(metadata, target, selectedIndex)) return []
  const selectedOffset = offsetAt(initialOffsets, selectedIndex)
  const updates: DragUpdate[] = [{
    index: selectedIndex,
    offset: { x: selectedOffset.x + deltaX, y: selectedOffset.y + deltaY },
  }]
  if (!pushAdjacent || target === 'dot') return updates

  if (target === 'word') {
    const selected = metadata.words[selectedIndex]
    if (selected === undefined) return []
    for (const candidate of metadata.words) {
      if (candidate.lineIndex !== selected.lineIndex || candidate.index <= selected.index) continue
      const initial = offsetAt(initialOffsets, candidate.index)
      updates.push({
        index: candidate.index,
        offset: { x: initial.x + deltaX, y: initial.y },
      })
    }
    return updates
  }

  const selected = metadata.letters[selectedIndex]
  if (selected === undefined) return []
  for (const candidate of metadata.letters) {
    if (candidate.wordIndex !== selected.wordIndex || candidate.index <= selected.index) continue
    const initial = offsetAt(initialOffsets, candidate.index)
    updates.push({
      index: candidate.index,
      offset: { x: initial.x + deltaX, y: initial.y },
    })
  }
  return updates
}
